import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  ExternalLink,
  Trash2,
  Search,
  FileDown,
  CalendarDays,
  Inbox,
  Loader2,
  Plus,
  BellRing,
  Timer,
  CalendarClock,
  History,
  AlertTriangle,
  Building2,
  MapPin,
  CalendarPlus,
  Keyboard,
  LayoutGrid,
  List,
  GripVertical,
  MoveDown,
  ChevronDown,
  Filter,
  Zap,
  Wallet,
  User,
  StickyNote,
  Award,
  Ban,
  MoreHorizontal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import './calendario18.scss';
import './seguimiento28.scss';

// ==========================================================
// Constantes de presentación
// ==========================================================
const SEACE_BUSCADOR =
  'https://prodapp2.seace.gob.pe/seacebus-uiwd-pub/buscadorPublico/buscadorPublico.xhtml';

const GOOGLE_IMPORT = 'https://calendar.google.com/calendar/u/0/r/settings/export';

const FUENTES = ['SEACE', 'CONTRATOS_MENORES'];

const FUENTE_LABEL = {
  SEACE: 'SEACE',
  CONTRATOS_MENORES: 'Contratos Menores'
};

const FUENTE_CHIP = {
  SEACE: 'SEACE',
  CONTRATOS_MENORES: 'Menores'
};

const FUENTE_CLASS = {
  SEACE: 'f-seace',
  CONTRATOS_MENORES: 'f-menores'
};

// Hitos que puede tener un proceso, en orden cronológico
const TIPOS = ['convocatoria', 'inicio_registro', 'fin_registro', 'presentacion_propuestas'];

const TIPO_LABEL = {
  convocatoria: 'Convocatoria',
  inicio_registro: 'Inicio de registro',
  fin_registro: 'Fin de registro',
  presentacion_propuestas: 'Presentación de ofertas'
};

const TIPO_CORTO = {
  convocatoria: 'Convocatoria',
  inicio_registro: 'Inicio registro',
  fin_registro: 'Fin de registro',
  presentacion_propuestas: 'Presentación'
};

const TIPO_CLASS = {
  convocatoria: 't-conv',
  inicio_registro: 't-inicio',
  fin_registro: 't-fin',
  presentacion_propuestas: 't-pres'
};

const WEEKDAYS_MINI = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const WEEKDAYS_MES = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];

// ==========================================================
// Etapa comercial (mockup 28m)
// El hito lo pone el SEACE; la etapa la pone el postor. Son dos relojes
// distintos, por eso la etapa no reutiliza los colores de los hitos.
// ==========================================================
const ETAPAS = ['revisada', 'negociando', 'vendida', 'perdida'];

// Todo lo que se guarda en el calendario entra directo aquí: «Sin estado» se
// retiró el 13-09-2026 porque el triaje previo no aportaba nada.
const ETAPA_INICIAL = 'revisada';

// Las filas guardadas antes de esa fecha pueden traer 'sin_estado', y la
// bitácora lo conserva para siempre: se lee como la etapa de entrada.
const normalizaEtapa = (etapa) => (ETAPAS.includes(etapa) ? etapa : ETAPA_INICIAL);

// Etapas todavía abiertas: son las que tiene sentido cronometrar
const ETAPAS_ABIERTAS = ['revisada', 'negociando'];

// La clave 'negociando' se mantiene en la base; solo cambió cómo se muestra.
const ETAPA_LABEL = {
  revisada: 'Revisada',
  negociando: 'Cotización enviada',
  vendida: 'Vendida',
  perdida: 'Perdida'
};

const ETAPA_CLASS = {
  revisada: 'e-rev',
  negociando: 'e-neg',
  vendida: 'e-ven',
  perdida: 'e-per'
};

const ETAPA_HEX = {
  revisada: '#14507d',
  negociando: '#b8791a',
  vendida: '#0f766e',
  perdida: '#b42318'
};

const ETAPA_AYUDA = {
  revisada: 'Guardada del SEACE: falta decidir si se cotiza',
  negociando: 'Cotización u oferta ya enviada, esperando respuesta',
  vendida: 'Buena pro',
  perdida: 'Cerrada sin adjudicación'
};

const ETAPAS_CERRADAS = ['vendida', 'perdida'];

const MOTIVOS = [
  { id: 'precio', label: 'Precio' },
  { id: 'plazo', label: 'Plazo' },
  { id: 'requisito', label: 'Requisito' },
  { id: 'no_presentado', label: 'No se presentó' }
];

const MOTIVO_LABEL = MOTIVOS.reduce((acc, m) => ({ ...acc, [m.id]: m.label.toLowerCase() }), { otro: 'otro' });

// Procesos marcados como presentados en la versión anterior: solo en este
// navegador. Se migran una vez a la etapa "negociando" (ver 28m).
const ENVIADOS_KEY = 'nuvia:calendario:enviados';
const MIGRADO_KEY = 'nuvia:seguimiento:migrado';

// ==========================================================
// Helpers de fecha (parse local para evitar corrimientos por UTC)
// ==========================================================
const two = (n) => String(n).padStart(2, '0');

const parseLocal = (raw) => {
  if (!raw) return null;
  const [datePart, timePartRaw = ''] = String(raw).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  let h = 0;
  let min = 0;
  if (timePartRaw) {
    const t = timePartRaw.split(/[.+Z]/)[0].split('-')[0];
    const [hh, mm] = t.split(':');
    h = Number(hh) || 0;
    min = Number(mm) || 0;
  }
  return new Date(y, m - 1, d, h, min);
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const sameDay = (a, b) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const diasDesdeHoy = (fecha) => Math.round((startOfDay(fecha) - startOfDay(new Date())) / 86400000);

const fmtHM = (d) => (d ? `${two(d.getHours())}:${two(d.getMinutes())}` : '');
const fmtFecha = (d) => (d ? `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}` : '—');
const fmtFechaHora = (d) => (d ? `${fmtFecha(d)} · ${fmtHM(d)}` : '—');

// Nivel de urgencia de un hito: rojo ≤1 día, ámbar ≤7, gris el resto
const nivelEvento = (fecha) => {
  if (!fecha) return 'far';
  if (fecha.getTime() < Date.now()) return 'old';
  const dias = diasDesdeHoy(fecha);
  if (dias <= 1) return 'urg';
  if (dias <= 7) return 'mid';
  return 'far';
};

// "en 4 h", "mañana", "en 5 d", "hace 3 d"
const etiquetaRelativa = (fecha) => {
  if (!fecha) return '';
  const ms = fecha.getTime() - Date.now();
  const dias = diasDesdeHoy(fecha);
  if (ms < 0) {
    const d = Math.abs(dias);
    return d === 0 ? 'pasó hoy' : `hace ${d} d`;
  }
  if (dias === 0) {
    const h = Math.floor(ms / 3600000);
    if (h < 1) return `en ${Math.max(1, Math.round(ms / 60000))} min`;
    return `en ${h} h`;
  }
  if (dias === 1) return 'mañana';
  return `en ${dias} d`;
};

// Cuenta atrás detallada ("4 h 12 min", "3 d 5 h")
const cuentaAtras = (fecha) => {
  if (!fecha) return null;
  const ms = fecha.getTime() - Date.now();
  if (ms <= 0) return { label: 'vencido', ms: 0 };
  const dias = Math.floor(ms / 86400000);
  const horas = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (dias >= 1) return { label: `${dias} d ${horas} h`, ms };
  if (horas >= 1) return { label: `${horas} h ${mins} min`, ms };
  return { label: `${mins} min`, ms };
};

// Etiqueta del separador de día en la agenda
const etiquetaDia = (fecha) => {
  const larga = fecha.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' });
  const dias = diasDesdeHoy(fecha);
  if (dias === 0) return { txt: `Hoy · ${larga}`, cls: 'is-today' };
  if (dias === 1) return { txt: `Mañana · ${larga}`, cls: 'is-tomorrow' };
  return { txt: larga, cls: '' };
};

// Matriz de 6 semanas para un mes
const matrizMes = (fecha) => {
  const primer = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
  const ultimo = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
  const out = [];
  for (let i = primer.getDay() - 1; i >= 0; i--) {
    const f = new Date(primer);
    f.setDate(f.getDate() - i - 1);
    out.push({ fecha: f, actual: false });
  }
  for (let d = 1; d <= ultimo.getDate(); d++) {
    out.push({ fecha: new Date(fecha.getFullYear(), fecha.getMonth(), d), actual: true });
  }
  for (let i = 1; out.length < 42; i++) {
    const f = new Date(ultimo);
    f.setDate(f.getDate() + i);
    out.push({ fecha: f, actual: false });
  }
  return out;
};

const leerEnviados = () => {
  try {
    const raw = window.localStorage.getItem(ENVIADOS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

// ==========================================================
// Helpers del pipeline
// ==========================================================
// "S/ 5.48M" para tarjetas y columnas
const fmtMontoCorto = (n) => {
  const v = Number(n);
  if (!v) return 'S/ 0';
  if (v >= 1e6) return `S/ ${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `S/ ${Math.round(v / 1e3)}K`;
  return `S/ ${Math.round(v)}`;
};

// "S/ 412,000" para el panel de detalle
const fmtMontoLargo = (n) => {
  const v = Number(n);
  if (!v && v !== 0) return '—';
  return `S/ ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

// "LP-SM-12-2026-MINEDU/1" → "LP"
const prefijoNomenclatura = (nom) => {
  const raw = String(nom || '').trim().split(/[-\s/]/)[0] || '?';
  return raw.slice(0, 4).toUpperCase();
};

// Punto 29: normalización visual; conserva los datos originales.
const SIGLAS_TITULO = new Set('RAPI MINSA PVC SEACE MTC ONPE INIA SUNAT SEDAPAR SEDAPAL ACFFAA ESSALUD USB UPS CPU GPS LED HDPE EPP'.split(' '));
export const tituloCorto = (texto) => {
  const limpio = String(texto || '').trim().replace(/^(?:ADQUISICI[ÓO]N|SERVICIO)\s+DE\s+/i, '');
  const normal = limpio.replace(/[\p{L}\d]+/gu, (palabra) => {
    if (SIGLAS_TITULO.has(palabra.toUpperCase()) || /\d/.test(palabra)) return palabra.toUpperCase();
    if (limpio !== limpio.toUpperCase() && /^[A-ZÁÉÍÓÚÑ]{2,5}$/.test(palabra)) return palabra;
    return palabra.toLocaleLowerCase('es-PE');
  });
  return normal ? normal[0].toUpperCase() + normal.slice(1) : 'Proceso por describir';
};

export const entidadCorta = (texto) => {
  const nombre = String(texto || '').trim();
  const entidades = {
    'SEGURO SOCIAL DE SALUD': 'EsSalud', ESSALUD: 'EsSalud',
    'MINISTERIO DE SALUD': 'MINSA',
    'MINISTERIO DE TRANSPORTES Y COMUNICACIONES': 'MTC',
    'OFICINA NACIONAL DE PROCESOS ELECTORALES': 'ONPE',
    'SERVICIO DE AGUA POTABLE Y ALCANTARILLADO DE LIMA': 'Sedapal',
    'SERVICIO DE AGUA POTABLE Y ALCANTARILLADO DE AREQUIPA': 'Sedapar',
    'INSTITUTO NACIONAL DE INNOVACION AGRARIA': 'INIA',
    'AGENCIA DE COMPRAS DE LAS FUERZAS ARMADAS': 'ACFFAA'
  };
  const clave = nombre.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  return entidades[clave] || tituloCorto(nombre)
    .replace(/^Municipalidad provincial de /i, 'Mun. Prov. de ')
    .replace(/^Municipalidad distrital de /i, 'Mun. de ');
};

const inicial = (nombre) => (String(nombre || '').trim()[0] || '—').toUpperCase();

// "hace 3 d", "hace 5 h", "hoy"
const haceCuanto = (fecha) => {
  if (!fecha) return '';
  const ms = Date.now() - fecha.getTime();
  if (ms < 3600000) return `hace ${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 86400000) return `hace ${Math.floor(ms / 3600000)} h`;
  return `hace ${Math.floor(ms / 86400000)} d`;
};

const fmtDiaCorto = (fecha) =>
  fecha ? fecha.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }).replace('.', '') : '';

// Fila por defecto: un proceso sin fila en `seguimiento` está en "sin estado"
const filaVacia = (userId, nomenclatura) => ({
  user_id: userId,
  nomenclatura,
  etapa: ETAPA_INICIAL,
  monto_estimado: null,
  responsable: null,
  motivo_cierre: null,
  orden: 0,
  notas: []
});

const Calendario = ({ user }) => {
  const navigate = useNavigate();

  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sincronizado, setSincronizado] = useState(null);

  const [view, setView] = useState('tablero'); // 'tablero' (28a) | 'cierres' (18a) | 'mes' (18b)
  const [fechaActual, setFechaActual] = useState(new Date());

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [fuentesOff, setFuentesOff] = useState({});
  const [tiposOff, setTiposOff] = useState({});
  const [tile, setTile] = useState(null); // null | 'urg' | 'sem' | 'mes' | 'old'
  const [diaSel, setDiaSel] = useState(null);
  const [verTodos, setVerTodos] = useState(false);

  // Selección / panel
  const [selKey, setSelKey] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // Pipeline (28)
  const [seg, setSeg] = useState({}); // nomenclatura -> fila de `seguimiento`
  const [segLog, setSegLog] = useState([]); // bitácora de cambios de etapa
  const [segError, setSegError] = useState(null); // la tabla todavía no existe
  const [dragNom, setDragNom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [menuEtapa, setMenuEtapa] = useState(null); // nomenclatura con el desplegable abierto
  const [modalPerdida, setModalPerdida] = useState(null); // { nomenclatura, motivo }
  const [colAbierta, setColAbierta] = useState({}); // etapa -> ver todas las tarjetas
  const [editCampo, setEditCampo] = useState(null); // { nomenclatura, campo, valor }
  const [notaAbierta, setNotaAbierta] = useState(false);
  const [notaTexto, setNotaTexto] = useState('');
  const [respFiltro, setRespFiltro] = useState('');
  const [fuenteFiltro, setFuenteFiltro] = useState('');
  const [soloUrgentes, setSoloUrgentes] = useState(false);
  const [accionesTarjeta, setAccionesTarjeta] = useState(null);
  const [ordenPipeline, setOrdenPipeline] = useState('cierre');
  const [relojPipeline, setRelojPipeline] = useState(Date.now);

  useEffect(() => {
    const timer = window.setInterval(() => setRelojPipeline(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const userId = user?.email || user?.id || user?.user_metadata?.email;

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadEventos = useCallback(
    async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('calendar_events')
          .select('*')
          .eq('user_id', userId)
          .order('fecha_evento', { ascending: true });
        if (error) throw error;
        setEventos((data || []).filter((evento) => FUENTES.includes(evento.fuente)));
        setSincronizado(Date.now());
      } catch (error) {
        console.error('Error cargando eventos del calendario:', error);
        showToast('No se pudieron cargar los eventos', 'error');
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    loadEventos();
  }, [loadEventos]);

  // ---------------------------------------------------------------
  // Pipeline: tabla `seguimiento` (una fila por proceso, no por hito)
  // ---------------------------------------------------------------
  const loadSeguimiento = useCallback(async () => {
    if (!userId) return;
    try {
      const [filas, logs] = await Promise.all([
        supabase.from('seguimiento').select('*').eq('user_id', userId),
        supabase
          .from('seguimiento_log')
          .select('*')
          .eq('user_id', userId)
          .order('creado_at', { ascending: true })
      ]);
      if (filas.error) throw filas.error;
      if (logs.error) throw logs.error;

      const map = {};
      (filas.data || []).forEach((f) => {
        map[f.nomenclatura] = { ...f, notas: Array.isArray(f.notas) ? f.notas : [] };
      });
      setSeg(map);
      setSegLog(logs.data || []);
      setSegError(null);
      return map;
    } catch (error) {
      console.error('Error cargando el pipeline de seguimiento:', error);
      setSegError(error.message || 'No se pudo leer la tabla seguimiento');
      return null;
    }
  }, [userId]);

  useEffect(() => {
    loadSeguimiento();
  }, [loadSeguimiento]);

  // Migración única del booleano viejo (`nuvia:calendario:enviados`) a etapas
  useEffect(() => {
    if (!userId || segError) return;
    if (window.localStorage.getItem(MIGRADO_KEY)) return;
    const viejos = leerEnviados();
    if (viejos.length === 0) {
      try { window.localStorage.setItem(MIGRADO_KEY, '1'); } catch (e) { /* modo privado */ }
      return;
    }
    (async () => {
      const pendientes = viejos.filter((nom) => !seg[nom]);
      if (pendientes.length === 0) {
        try { window.localStorage.setItem(MIGRADO_KEY, '1'); } catch (e) { /* modo privado */ }
        return;
      }
      const filas = pendientes.map((nom) => ({ ...filaVacia(userId, nom), etapa: 'negociando' }));
      const { error } = await supabase.from('seguimiento').upsert(filas, { onConflict: 'user_id,nomenclatura' });
      if (error) {
        console.error('No se pudo migrar el estado de presentados:', error);
        return;
      }
      try { window.localStorage.setItem(MIGRADO_KEY, '1'); } catch (e) { /* modo privado */ }
      await loadSeguimiento();
      showToast(`${filas.length} proceso(s) presentados pasaron a «Negociando»`, 'info');
    })();
    // Solo debe correr una vez, cuando ya se leyó el pipeline
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, segError]);

  const filaDe = useCallback(
    (nomenclatura) => seg[nomenclatura] || filaVacia(userId, nomenclatura),
    [seg, userId]
  );

  const etapaDe = useCallback((nomenclatura) => normalizaEtapa((seg[nomenclatura] || {}).etapa), [seg]);

  // Guarda cambios del pipeline. `log` registra además el salto de etapa.
  const guardarSeg = async (nomenclatura, cambios, opciones = {}) => {
    if (!userId) return;
    const previo = filaDe(nomenclatura);
    const fila = {
      ...previo,
      ...cambios,
      user_id: userId,
      nomenclatura,
      notas: cambios.notas || previo.notas || []
    };
    delete fila.updated_at; // lo pone el trigger de Postgres

    setSeg((prev) => ({ ...prev, [nomenclatura]: { ...fila, updated_at: new Date().toISOString() } }));

    try {
      const { error } = await supabase
        .from('seguimiento')
        .upsert(fila, { onConflict: 'user_id,nomenclatura' });
      if (error) throw error;
      setSegError(null);

      if (opciones.log && previo.etapa !== fila.etapa) {
        const entrada = {
          user_id: userId,
          nomenclatura,
          etapa_anterior: previo.etapa,
          etapa_nueva: fila.etapa,
          autor: userId,
          creado_at: new Date().toISOString()
        };
        const { error: errLog } = await supabase.from('seguimiento_log').insert(entrada);
        if (errLog) console.warn('No se pudo escribir la bitácora:', errLog);
        else setSegLog((prev) => [...prev, entrada]);
      }
    } catch (error) {
      console.error('Error guardando el seguimiento:', error);
      setSeg((prev) => {
        const next = { ...prev };
        if (seg[nomenclatura]) next[nomenclatura] = seg[nomenclatura];
        else delete next[nomenclatura];
        return next;
      });
      setSegError(error.message || 'No se pudo guardar');
      showToast('No se pudo guardar el cambio de etapa', 'error');
    }
  };

  // Cambia la etapa. "Perdida" pide motivo antes de confirmar.
  const cambiarEtapa = (nomenclatura, etapa, motivo) => {
    if (etapa === 'perdida' && !motivo) {
      setModalPerdida({ nomenclatura, motivo: filaDe(nomenclatura).motivo_cierre || 'precio' });
      setMenuEtapa(null);
      return;
    }
    const cambios = { etapa, motivo_cierre: etapa === 'perdida' ? motivo || 'otro' : null };
    guardarSeg(nomenclatura, cambios, { log: true });
    setMenuEtapa(null);
    if (etapa !== etapaDe(nomenclatura)) showToast(`Movido a «${ETAPA_LABEL[etapa]}»`, 'success');
  };

  const guardarNota = (nomenclatura, texto) => {
    const limpio = texto.trim();
    if (!limpio) return;
    const notas = [
      ...(filaDe(nomenclatura).notas || []),
      { texto: limpio, autor: userId, fecha: new Date().toISOString() }
    ];
    guardarSeg(nomenclatura, { notas });
    setNotaTexto('');
    setNotaAbierta(false);
  };

  const eliminarProceso = async (nomenclatura) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('user_id', userId)
        .eq('nomenclatura', nomenclatura);
      if (error) throw error;
      await supabase.from('seguimiento').delete().eq('user_id', userId).eq('nomenclatura', nomenclatura);
      setEventos((prev) => prev.filter((e) => e.nomenclatura !== nomenclatura));
      setSeg((prev) => {
        const next = { ...prev };
        delete next[nomenclatura];
        return next;
      });
      setSelKey(null);
      setDrawerOpen(false);
      showToast('Proceso quitado del seguimiento', 'success');
    } catch (error) {
      console.error('Error eliminando proceso:', error);
      showToast('Error al quitar el proceso', 'error');
    }
  };

  // ---------------------------------------------------------------
  // Procesos y eventos normalizados
  // ---------------------------------------------------------------
  const procesos = useMemo(() => {
    const map = new Map();
    eventos.forEach((ev) => {
      const key = ev.nomenclatura;
      if (!map.has(key)) {
        map.set(key, {
          nomenclatura: ev.nomenclatura,
          entidad: ev.entidad,
          departamento: ev.departamento,
          objeto: ev.objeto,
          fuente: FUENTES.includes(ev.fuente) ? ev.fuente : 'SEACE',
          descripcion_proceso: ev.descripcion_proceso,
          creado: null,
          fechas: {}
        });
      }
      const p = map.get(key);
      const fecha = parseLocal(ev.fecha_evento);
      if (fecha && TIPOS.includes(ev.tipo_evento)) p.fechas[ev.tipo_evento] = fecha;
      // El alta en el calendario es el arranque del reloj de "sin estado"
      const alta = ev.created_at ? new Date(ev.created_at) : null;
      if (alta && !Number.isNaN(alta.getTime()) && (!p.creado || alta < p.creado)) p.creado = alta;
    });
    return Array.from(map.values());
  }, [eventos]);

  // Lista plana de hitos, uno por fecha
  const todosLosEventos = useMemo(() => {
    const out = [];
    procesos.forEach((p) => {
      TIPOS.forEach((tipo) => {
        const fecha = p.fechas[tipo];
        if (!fecha) return;
        out.push({
          key: `${p.nomenclatura}|${tipo}`,
          fecha,
          tipo,
          proceso: p
        });
      });
    });
    out.sort((a, b) => a.fecha - b.fecha);
    return out;
  }, [procesos]);

  // Filtro base: fuente + tipo + búsqueda (sin tiles ni día)
  const eventosBase = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return todosLosEventos.filter((ev) => {
      if (fuentesOff[ev.proceso.fuente]) return false;
      if (tiposOff[ev.tipo]) return false;
      if (!q) return true;
      return (
        (ev.proceso.nomenclatura || '').toLowerCase().includes(q) ||
        (ev.proceso.entidad || '').toLowerCase().includes(q) ||
        (ev.proceso.descripcion_proceso || '').toLowerCase().includes(q)
      );
    });
  }, [todosLosEventos, fuentesOff, tiposOff, busqueda]);

  // Resumen para las cuatro tarjetas de arriba
  const resumen = useMemo(() => {
    const ahora = Date.now();
    const finMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999);

    const urg = [];
    const sem = [];
    const mes = [];
    const old = [];

    eventosBase.forEach((ev) => {
      const t = ev.fecha.getTime();
      if (t < ahora) {
        old.push(ev);
        return;
      }
      const dias = diasDesdeHoy(ev.fecha);
      if (dias <= 1) urg.push(ev);
      if (dias <= 7) sem.push(ev);
      else if (ev.fecha <= finMes) mes.push(ev);
    });

    const proximo = urg.length ? cuentaAtras(urg[0].fecha) : null;
    const fines = sem.filter((e) => e.tipo === 'fin_registro').length;
    const ofertas = sem.filter((e) => e.tipo === 'presentacion_propuestas').length;
    const diasMes = new Set(mes.map((e) => e.fecha.toDateString())).size;

    return {
      urg: urg.length,
      proximo,
      sem: sem.length,
      fines,
      ofertas,
      mes: mes.length,
      diasMes,
      old: old.length
    };
  }, [eventosBase]);

  // Lista visible de la agenda (aplica tarjeta y día seleccionado)
  const eventosVisibles = useMemo(() => {
    const ahora = Date.now();
    const finMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999);

    let lista = eventosBase.filter((ev) => {
      const t = ev.fecha.getTime();
      if (tile === 'old') return t < ahora;
      if (t < ahora) return false;
      const dias = diasDesdeHoy(ev.fecha);
      if (tile === 'urg') return dias <= 1;
      if (tile === 'sem') return dias <= 7;
      if (tile === 'mes') return dias > 7 && ev.fecha <= finMes;
      return true;
    });

    if (diaSel) lista = lista.filter((ev) => sameDay(ev.fecha, diaSel));
    if (tile === 'old') lista = [...lista].reverse();
    return lista;
  }, [eventosBase, tile, diaSel]);

  const eventosMostrados = verTodos ? eventosVisibles : eventosVisibles.slice(0, 6);

  // Agrupar por día para la agenda
  const gruposAgenda = useMemo(() => {
    const map = new Map();
    eventosMostrados.forEach((ev) => {
      const k = ev.fecha.toDateString();
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    });
    return Array.from(map.entries());
  }, [eventosMostrados]);

  // Evento más inminente (para la franja de alerta)
  const inminente = useMemo(() => {
    const prox = eventosBase.find((ev) => ev.fecha.getTime() > Date.now());
    if (!prox) return null;
    const cd = cuentaAtras(prox.fecha);
    if (!cd || cd.ms > 86400000) return null;
    return { ev: prox, cd };
  }, [eventosBase]);

  // Eventos por día del mes visible (mini-mes y rejilla)
  const eventosDelMes = useMemo(() => {
    const map = new Map();
    eventosBase.forEach((ev) => {
      if (ev.fecha.getFullYear() !== fechaActual.getFullYear() || ev.fecha.getMonth() !== fechaActual.getMonth()) return;
      const k = ev.fecha.toDateString();
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    });
    return map;
  }, [eventosBase, fechaActual]);

  // Todos los hitos del mes visible, en orden (navegación de la vista mes)
  const eventosMesPlano = useMemo(() => {
    const out = [];
    eventosDelMes.forEach((arr) => out.push(...arr));
    out.sort((a, b) => a.fecha - b.fecha);
    return out;
  }, [eventosDelMes]);

  const dias = useMemo(() => matrizMes(fechaActual), [fechaActual]);

  const conteoFuente = useMemo(() => {
    const c = {};
    FUENTES.forEach((f) => {
      c[f] = procesos.filter((p) => p.fuente === f).length;
    });
    return c;
  }, [procesos]);

  const conteoTipo = useMemo(() => {
    const c = {};
    TIPOS.forEach((t) => {
      c[t] = todosLosEventos.filter((ev) => ev.tipo === t).length;
    });
    return c;
  }, [todosLosEventos]);

  const procesosConFechas = useMemo(
    () => procesos.filter((p) => Object.keys(p.fechas).length > 0).length,
    [procesos]
  );

  // ---------------------------------------------------------------
  // Pipeline: el mismo conjunto de procesos, agrupado por etapa (28a)
  // ---------------------------------------------------------------
  // Cada proceso con su fila de seguimiento y su hito más próximo resuelto
  const pipeline = useMemo(() => {
    const ahora = relojPipeline;
    return procesos.map((p) => {
      const fila = seg[p.nomenclatura] || filaVacia(userId, p.nomenclatura);
      const futuros = TIPOS.filter((t) => p.fechas[t] && p.fechas[t].getTime() > ahora).sort(
        (a, b) => p.fechas[a] - p.fechas[b]
      );
      const proximo = futuros[0] || null;
      const cerradas = TIPOS.filter((t) => p.fechas[t]).sort((a, b) => p.fechas[b] - p.fechas[a]);
      return {
        proceso: p,
        fila,
        etapa: normalizaEtapa(fila.etapa),
        monto: Number(fila.monto_estimado) || 0,
        proximo,
        fechaProxima: proximo ? p.fechas[proximo] : null,
        ultimoHito: cerradas[0] ? p.fechas[cerradas[0]] : null,
        nivel: proximo ? nivelEvento(p.fechas[proximo]) : 'none'
      };
    });
  }, [procesos, seg, userId, relojPipeline]);

  const responsables = useMemo(() => {
    const set = new Set();
    pipeline.forEach(({ fila }) => {
      if (fila.responsable) set.add(fila.responsable);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [pipeline]);

  const pipelineFiltrado = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return pipeline.filter((it) => {
      const p = it.proceso;
      if (fuenteFiltro && p.fuente !== fuenteFiltro) return false;
      if (respFiltro && (it.fila.responsable || '') !== respFiltro) return false;
      if (soloUrgentes) {
        if (ETAPAS_CERRADAS.includes(it.etapa) || !it.fechaProxima) return false;
        if (it.fechaProxima.getTime() - Date.now() > 7 * 86400000) return false;
      }
      if (!q) return true;
      return (
        (p.nomenclatura || '').toLowerCase().includes(q) ||
        (p.entidad || '').toLowerCase().includes(q) ||
        (p.descripcion_proceso || '').toLowerCase().includes(q)
      );
    });
  }, [pipeline, fuenteFiltro, respFiltro, soloUrgentes, busqueda]);

  // Una columna por etapa. Orden dentro de la columna: el guardado en `orden`
  // y, a igualdad, el hito más próximo primero.
  const columnas = useMemo(() => {
    const out = {};
    ETAPAS.forEach((e) => {
      out[e] = [];
    });
    pipelineFiltrado.forEach((it) => {
      (out[it.etapa] || out[ETAPA_INICIAL]).push(it);
    });
    ETAPAS.forEach((e) => {
      out[e].sort((a, b) => {
        const oa = Number(a.fila.orden) || 0;
        const ob = Number(b.fila.orden) || 0;
        if (ordenPipeline === 'guardado' && oa !== ob) return oa - ob;
        if (a.fechaProxima && b.fechaProxima) return a.fechaProxima - b.fechaProxima;
        if (a.fechaProxima) return -1;
        if (b.fechaProxima) return 1;
        return (a.proceso.nomenclatura || '').localeCompare(b.proceso.nomenclatura || '');
      });
    });
    return out;
  }, [pipelineFiltrado, ordenPipeline]);

  // Los cuatro indicadores de la cabecera del tablero
  const kpi = useMemo(() => {
    // Cuántas convocatorias hay guardadas y de dónde vienen. Se mide sobre el
    // pipeline entero, como el resto de indicadores: los filtros de la barra
    // recortan el tablero, no el recuento.
    const porFuente = {};
    pipeline.forEach((it) => {
      const f = it.proceso.fuente;
      if (f) porFuente[f] = (porFuente[f] || 0) + 1;
    });
    const abiertas = pipeline.filter((it) => !ETAPAS_CERRADAS.includes(it.etapa)).length;

    // Cierres inminentes y en qué etapa están
    const urgentes = pipeline.filter((it) => it.fechaProxima && diasDesdeHoy(it.fechaProxima) <= 1);
    const etapaUrgente = {};
    urgentes.forEach((it) => {
      etapaUrgente[it.etapa] = (etapaUrgente[it.etapa] || 0) + 1;
    });
    const etapaUrgenteTop = Object.entries(etapaUrgente).sort((a, b) => b[1] - a[1])[0];

    // Cerradas dentro del mes en curso (la fecha la da la bitácora)
    const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    const cierreDe = (nom, fila) => {
      const entradas = segLog.filter((l) => l.nomenclatura === nom && ETAPAS_CERRADAS.includes(l.etapa_nueva));
      const ultima = entradas[entradas.length - 1];
      const raw = ultima ? ultima.creado_at : fila.updated_at;
      const f = raw ? new Date(raw) : null;
      return f && !Number.isNaN(f.getTime()) ? f : null;
    };
    let ganadas = 0;
    let perdidas = 0;
    const motivos = {};
    pipeline.forEach((it) => {
      if (!ETAPAS_CERRADAS.includes(it.etapa)) return;
      const f = cierreDe(it.proceso.nomenclatura, it.fila);
      if (!f || f.getTime() < inicioMes) return;
      if (it.etapa === 'vendida') ganadas += 1;
      else {
        perdidas += 1;
        const m = it.fila.motivo_cierre || 'otro';
        motivos[m] = (motivos[m] || 0) + 1;
      }
    });
    const motivoTop = Object.entries(motivos).sort((a, b) => b[1] - a[1])[0];
    const cerradas = ganadas + perdidas;

    // Tiempo medio en cada etapa abierta, reconstruido con la bitácora
    const suma = {};
    const cuenta = {};
    ETAPAS.forEach((e) => {
      suma[e] = 0;
      cuenta[e] = 0;
    });
    pipeline.forEach((it) => {
      const arranque = it.proceso.creado ? it.proceso.creado.getTime() : null;
      if (!arranque) return;
      const entradas = segLog
        .filter((l) => l.nomenclatura === it.proceso.nomenclatura)
        .map((l) => ({ ...l, t: new Date(l.creado_at).getTime() }))
        .filter((l) => !Number.isNaN(l.t))
        .sort((a, b) => a.t - b.t);

      let etapaActual = ETAPA_INICIAL;
      let desde = arranque;
      entradas.forEach((l) => {
        const dur = l.t - desde;
        if (dur > 0) {
          suma[etapaActual] += dur;
          cuenta[etapaActual] += 1;
        }
        etapaActual = normalizaEtapa(l.etapa_nueva);
        desde = l.t;
      });
      if (ETAPAS_ABIERTAS.includes(etapaActual)) {
        const dur = Date.now() - desde;
        if (dur > 0) {
          suma[etapaActual] += dur;
          cuenta[etapaActual] += 1;
        }
      }
    });
    const tiempos = ETAPAS_ABIERTAS.map((e) => ({
      etapa: e,
      dias: cuenta[e] ? suma[e] / cuenta[e] / 86400000 : null
    }));
    const maxDias = Math.max(1, ...tiempos.map((t) => t.dias || 0));

    return {
      total: pipeline.length,
      abiertas,
      porFuente,
      urgentes: urgentes.length,
      etapaUrgenteTop,
      ganadas,
      perdidas,
      cerradas,
      tasa: cerradas ? Math.round((ganadas / cerradas) * 100) : null,
      motivoTop,
      tiempos,
      maxDias
    };
  }, [pipeline, segLog]);

  // ---------------------------------------------------------------
  // Selección y navegación por teclado
  // ---------------------------------------------------------------
  // Recorrido del tablero: columna por columna, en el orden en que se ven
  const tableroNav = useMemo(() => {
    const out = [];
    ETAPAS.forEach((e) => {
      (columnas[e] || []).forEach((it) => {
        const tipo = it.proximo || TIPOS.find((t) => it.proceso.fechas[t]);
        if (!tipo) return;
        const ev = todosLosEventos.find((x) => x.key === `${it.proceso.nomenclatura}|${tipo}`);
        if (ev) out.push(ev);
      });
    });
    return out;
  }, [columnas, todosLosEventos]);

  // Lista sobre la que navegan J/K: el tablero en 28a, la agenda en 18a, el mes en 18b
  const listaNav = view === 'mes' ? eventosMesPlano : view === 'tablero' ? tableroNav : eventosVisibles;

  const seleccionado = useMemo(
    () => todosLosEventos.find((ev) => ev.key === selKey) || null,
    [todosLosEventos, selKey]
  );

  const indiceSel = useMemo(() => listaNav.findIndex((ev) => ev.key === selKey), [listaNav, selKey]);

  const abrirEvento = (ev, comoDrawer) => {
    setSelKey(ev.key);
    if (comoDrawer) setDrawerOpen(true);
  };

  const cerrarPanel = () => {
    setDrawerOpen(false);
    if (view === 'mes') setSelKey(null);
  };

  const navEvento = useCallback(
    (delta) => {
      if (listaNav.length === 0) return;
      const base = indiceSel < 0 ? 0 : indiceSel + delta;
      const n = Math.min(listaNav.length - 1, Math.max(0, base));
      setSelKey(listaNav[n].key);
    },
    [listaNav, indiceSel]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (!selKey) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const k = e.key.toLowerCase();
      if (k === 'j') { e.preventDefault(); navEvento(1); }
      else if (k === 'k') { e.preventDefault(); navEvento(-1); }
      else if (e.key === 'Escape') { setDrawerOpen(false); setSelKey(null); setMenuEtapa(null); }
      else if (e.key >= '1' && e.key <= String(ETAPAS.length)) {
        // 1–4 cambia la etapa comercial del proceso seleccionado
        const nom = selKey.split('|')[0];
        if (!nom) return;
        e.preventDefault();
        cambiarEtapa(nom, ETAPAS[Number(e.key) - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // cambiarEtapa se recrea en cada render; basta con la selección y la lista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, navEvento, seg]);

  // Un clic fuera cierra el desplegable de etapa
  useEffect(() => {
    if (!menuEtapa) return undefined;
    const cerrar = () => setMenuEtapa(null);
    document.addEventListener('click', cerrar);
    return () => document.removeEventListener('click', cerrar);
  }, [menuEtapa]);

  // En la vista mes siempre hay panel: selecciona el hito más próximo
  useEffect(() => {
    if (view !== 'mes' || selKey) return;
    const prox = eventosBase.find((ev) => ev.fecha.getTime() > Date.now()) || eventosBase[0];
    if (prox) setSelKey(prox.key);
  }, [view, selKey, eventosBase]);

  // ---------------------------------------------------------------
  // Exportación
  // ---------------------------------------------------------------
  const exportarCSV = () => {
    if (eventosVisibles.length === 0) {
      showToast('No hay eventos para exportar', 'warning');
      return;
    }
    const head = ['Fecha', 'Hora', 'Hito', 'Proceso', 'Entidad', 'Fuente', 'Departamento', 'Objeto', 'Descripción'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = eventosVisibles.map((ev) =>
      [
        fmtFecha(ev.fecha),
        fmtHM(ev.fecha),
        TIPO_LABEL[ev.tipo],
        ev.proceso.nomenclatura,
        ev.proceso.entidad,
        FUENTE_CHIP[ev.proceso.fuente] || ev.proceso.fuente,
        ev.proceso.departamento,
        ev.proceso.objeto,
        ev.proceso.descripcion_proceso
      ]
        .map(esc)
        .join(',')
    );
    const csv = [head.map(esc).join(','), ...lines].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mis-procesos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exportados ${eventosVisibles.length} eventos`, 'success');
  };

  // Genera un .ics con avisos 3 días y 1 día antes de cada hito
  const exportarICS = () => {
    const lista = eventosBase.filter((ev) => ev.fecha.getTime() > Date.now());
    if (lista.length === 0) {
      showToast('No hay fechas futuras que exportar', 'warning');
      return;
    }
    const utc = (d) => `${d.getUTCFullYear()}${two(d.getUTCMonth() + 1)}${two(d.getUTCDate())}T${two(d.getUTCHours())}${two(d.getUTCMinutes())}00Z`;
    const esc = (v) => String(v ?? '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
    const ahora = utc(new Date());

    const vevents = lista.map((ev) => {
      const fin = new Date(ev.fecha.getTime() + 30 * 60000);
      const titulo = `${TIPO_CORTO[ev.tipo]} · ${ev.proceso.nomenclatura}`;
      const desc = [
        ev.proceso.descripcion_proceso,
        ev.proceso.entidad,
        [ev.proceso.departamento, ev.proceso.objeto].filter(Boolean).join(' · ')
      ]
        .filter(Boolean)
        .join('\n');
      return [
        'BEGIN:VEVENT',
        `UID:${ev.proceso.nomenclatura}-${ev.tipo}@nuvia`.replace(/\s+/g, ''),
        `DTSTAMP:${ahora}`,
        `DTSTART:${utc(ev.fecha)}`,
        `DTEND:${utc(fin)}`,
        `SUMMARY:${esc(titulo)}`,
        `DESCRIPTION:${esc(desc)}`,
        `LOCATION:${esc(ev.proceso.departamento || 'Perú')}`,
        'BEGIN:VALARM',
        'TRIGGER:-P3D',
        'ACTION:DISPLAY',
        `DESCRIPTION:${esc(`Faltan 3 días — ${titulo}`)}`,
        'END:VALARM',
        'BEGIN:VALARM',
        'TRIGGER:-P1D',
        'ACTION:DISPLAY',
        `DESCRIPTION:${esc(`Falta 1 día — ${titulo}`)}`,
        'END:VALARM',
        'END:VEVENT'
      ].join('\r\n');
    });

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Nuvia//Calendario de Procesos//ES',
      'CALSCALE:GREGORIAN',
      ...vevents,
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mis-procesos-${new Date().toISOString().slice(0, 10)}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`${lista.length} fechas exportadas con avisos`, 'success');
  };

  const cambiarMes = (dir) => {
    const n = new Date(fechaActual);
    n.setDate(1);
    n.setMonth(n.getMonth() + dir);
    setFechaActual(n);
  };

  const toggleFuente = (f) => setFuentesOff((prev) => ({ ...prev, [f]: !prev[f] }));
  const toggleTipo = (t) => setTiposOff((prev) => ({ ...prev, [t]: !prev[t] }));

  const clicTile = (id) => {
    setTile((prev) => (prev === id ? null : id));
    setDiaSel(null);
    setVerTodos(false);
  };

  const clicDiaMini = (fecha, tieneEventos) => {
    if (!tieneEventos) return;
    setDiaSel((prev) => (sameDay(prev, fecha) ? null : fecha));
    setTile(null);
    setVerTodos(true);
  };

  // ---------------------------------------------------------------
  // Arrastre del tablero (HTML5 nativo: sin dependencias nuevas)
  // ---------------------------------------------------------------
  const onDragStart = (e, nomenclatura) => {
    setDragNom(nomenclatura);
    try {
      e.dataTransfer.setData('text/plain', nomenclatura);
      e.dataTransfer.effectAllowed = 'move';
    } catch (err) {
      /* algunos navegadores no dejan escribir el dataTransfer */
    }
  };

  const onDragEnd = () => {
    setDragNom(null);
    setDragOver(null);
  };

  // Toda la columna acepta el soltado, no solo una franja: el dragover se
  // atiende en el contenedor y las tarjetas quedan sin pointer-events mientras
  // dura el arrastre, así el puntero nunca "cae" en un hijo.
  const onDragOverCol = (e, etapa) => {
    if (!dragNom) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== etapa) setDragOver(etapa);
  };

  // Solo se apaga el resalte al salir de verdad de la columna, no al pasar
  // por encima de una tarjeta hija (dragleave burbujea).
  const onDragLeaveCol = (e, etapa) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver((prev) => (prev === etapa ? null : prev));
  };

  const onDropCol = (e, etapa) => {
    e.preventDefault();
    const nom = dragNom || e.dataTransfer.getData('text/plain');
    setDragNom(null);
    setDragOver(null);
    if (!nom) return;
    const actual = etapaDe(nom);
    if (actual === etapa) return;
    // El orden dentro de la columna destino: al final
    const orden = (columnas[etapa] || []).length;
    if (etapa === 'perdida') {
      setModalPerdida({ nomenclatura: nom, motivo: filaDe(nom).motivo_cierre || 'precio', orden });
      return;
    }
    guardarSeg(nom, { etapa, motivo_cierre: null, orden }, { log: true });
    showToast(`«${prefijoNomenclatura(nom)}…» pasó a «${ETAPA_LABEL[etapa]}»`, 'success');
  };

  const confirmarPerdida = () => {
    if (!modalPerdida) return;
    const { nomenclatura, motivo, orden } = modalPerdida;
    guardarSeg(
      nomenclatura,
      { etapa: 'perdida', motivo_cierre: motivo || 'otro', orden: orden ?? 0 },
      { log: true }
    );
    setModalPerdida(null);
    showToast(`Marcado como perdida · motivo: ${MOTIVO_LABEL[motivo] || motivo}`, 'info');
  };

  // Abre el proceso en el panel usando su hito más próximo
  const abrirProceso = (it) => {
    const tipo = it.proximo || TIPOS.find((t) => it.proceso.fechas[t]);
    if (!tipo) return;
    setSelKey(`${it.proceso.nomenclatura}|${tipo}`);
    setDrawerOpen(true);
  };

  const guardarCampo = () => {
    if (!editCampo) return;
    const { nomenclatura, campo, valor } = editCampo;
    if (campo === 'monto_estimado') {
      const limpio = String(valor).replace(/[^\d.]/g, '');
      guardarSeg(nomenclatura, { monto_estimado: limpio ? Number(limpio) : null });
    } else {
      guardarSeg(nomenclatura, { responsable: String(valor).trim() || null });
    }
    setEditCampo(null);
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  if (loading) {
    return (
      <div className="cal18">
        <div className="cal18-loading">
          <Loader2 size={30} className="cal18-spin" />
          <span>Cargando calendario…</span>
        </div>
      </div>
    );
  }

  const horaSync = sincronizado
    ? new Date(sincronizado).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;

  const hoy = new Date();
  const eventosHoy = eventosBase.filter((ev) => sameDay(ev.fecha, hoy)).length;
  const cierresSemana = eventosBase.filter((ev) => {
    const d = diasDesdeHoy(ev.fecha);
    return ev.fecha.getTime() >= Date.now() && d <= 7 && ev.tipo !== 'convocatoria';
  }).length;

  const segmentado = (
    <div className="cal18-seg sg28-seg3">
      <button className={view === 'tablero' ? 'is-on' : ''} onClick={() => setView('tablero')} title="Tablero por etapa">
        <LayoutGrid size={15} />
        <span>Tablero</span>
      </button>
      <button className={view === 'cierres' ? 'is-on' : ''} onClick={() => setView('cierres')} title="Próximos cierres">
        <List size={15} />
        <span>Próximos cierres</span>
      </button>
      <button className={view === 'mes' ? 'is-on' : ''} onClick={() => setView('mes')} title="Vista mes">
        <CalendarDays size={15} />
        <span>Mes</span>
      </button>
    </div>
  );

  // Píldora de etapa con desplegable. Es el control que sustituye al viejo
  // booleano "Marcar enviado": cinco etapas en vez de sí/no.
  const renderPill = (nomenclatura, extra = '') => {
    const etapa = etapaDe(nomenclatura);
    const abierto = menuEtapa === nomenclatura;
    return (
      <span className={`sg28-pillwrap ${extra}`} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`sg28-pill ${ETAPA_CLASS[etapa]}`}
          onClick={() => setMenuEtapa(abierto ? null : nomenclatura)}
          title={ETAPA_AYUDA[etapa]}
        >
          <i />
          {ETAPA_LABEL[etapa]}
          <ChevronDown size={13} />
        </button>

        {abierto && (
          <div className="sg28-menu">
            {ETAPAS.map((e) => (
              <button
                key={e}
                type="button"
                className={`${ETAPA_CLASS[e]} ${e === etapa ? 'is-on' : ''}`}
                onClick={() => cambiarEtapa(nomenclatura, e)}
                title={ETAPA_AYUDA[e]}
              >
                <i />
                {ETAPA_LABEL[e]}
                {e === 'perdida' ? '…' : ''}
                {e === etapa && <Check size={13} strokeWidth={3} />}
              </button>
            ))}
          </div>
        )}
      </span>
    );
  };

  const leyenda = (
    <div className="cal18-legend">
      <span><i style={{ background: '#0f766e' }} />Inicio registro</span>
      <span><i style={{ background: '#14507d' }} />Fin de registro</span>
      <span><i style={{ background: '#b42318' }} />Presentación</span>
    </div>
  );

  // --- Panel de detalle, compartido por ambas vistas ---
  const renderDetalle = () => {
    if (!seleccionado) {
      return (
        <div className="cal18-detail">
          <div className="cal18-detail-head">
            <b>Detalle del proceso</b>
          </div>
          <div className="cal18-empty">
            <div className="cal18-empty-ic"><CalendarDays size={22} /></div>
            <h4>Sin proceso seleccionado</h4>
            <p>Elige una fecha del calendario para ver el detalle del proceso.</p>
          </div>
        </div>
      );
    }

    const p = seleccionado.proceso;
    const fila = filaDe(p.nomenclatura);
    const etapa = normalizaEtapa(fila.etapa);
    const idxEtapa = ETAPAS.indexOf(etapa);
    // La barra de etapa se llena hasta la actual; "perdida" salta "vendida"
    const llenos = etapa === 'perdida'
      ? ETAPAS.map((_, i) => i).filter((i) => ETAPAS[i] !== 'vendida')
      : ETAPAS.map((_, i) => i).filter((i) => i <= idxEtapa);
    const ultimoCambio = [...segLog].reverse().find((l) => l.nomenclatura === p.nomenclatura);
    const fechaCambio = ultimoCambio ? new Date(ultimoCambio.creado_at) : null;
    const notas = fila.notas || [];
    const editando = editCampo && editCampo.origen !== 'tarjeta' && editCampo.nomenclatura === p.nomenclatura ? editCampo : null;
    const hitos = TIPOS.filter((t) => p.fechas[t]);
    const proximoHito = hitos.find((t) => p.fechas[t].getTime() > Date.now());
    const cd = proximoHito ? cuentaAtras(p.fechas[proximoHito]) : null;
    const nivelProximo = proximoHito ? nivelEvento(p.fechas[proximoHito]) : 'old';

    // Progreso de la cuenta atrás: sobre los últimos 7 días antes del hito
    const pct = cd ? Math.min(100, Math.max(4, 100 - (cd.ms / (7 * 86400000)) * 100)) : 100;

    return (
      <div className="cal18-detail">
        <div className="cal18-detail-head">
          <b>Detalle del proceso</b>
          {indiceSel >= 0 && (
            <span>
              {indiceSel + 1} de {listaNav.length}
            </span>
          )}
          <button className="cal18-icobtn sm" onClick={cerrarPanel} title="Cerrar (Esc)">
            <X size={15} />
          </button>
        </div>

        <div className="cal18-detail-body">
          <div className="cal18-detail-tags">
            <span className={`cal18-tag ${FUENTE_CLASS[p.fuente] || 'f-otro'}`}>
              {FUENTE_CHIP[p.fuente] || p.fuente}
            </span>
            {p.objeto && <span className="cal18-tag f-otro">{p.objeto}</span>}
            {cd ? (
              <span className={`cal18-urg lv-${nivelProximo}`}>
                <Timer size={12} />
                {TIPO_CORTO[proximoHito]} en {cd.label}
              </span>
            ) : (
              <span className="cal18-urg lv-old">Sin fechas futuras</span>
            )}
          </div>

          <div className="cal18-detail-code">{p.nomenclatura}</div>
          {p.descripcion_proceso && <div className="cal18-detail-desc">{p.descripcion_proceso}</div>}

          <div className="cal18-detail-section" style={{ marginTop: 18 }}>ETAPA COMERCIAL</div>
          <div className="sg28-stagebar">
            {ETAPAS.map((e, i) => (
              <span key={e} style={llenos.includes(i) ? { background: ETAPA_HEX[e] } : undefined} />
            ))}
          </div>
          <div className="sg28-stagerow">
            {renderPill(p.nomenclatura)}
            {fechaCambio && (
              <small>
                {haceCuanto(fechaCambio)}
                {ultimoCambio.autor ? ` · por ${ultimoCambio.autor}` : ''}
              </small>
            )}
          </div>
          <div className="sg28-stagecta">
            <button
              type="button"
              className="is-win"
              disabled={etapa === 'vendida'}
              onClick={() => cambiarEtapa(p.nomenclatura, 'vendida')}
            >
              <Award size={14} style={{ verticalAlign: '-2px', marginRight: 5 }} />
              Marcar ganada
            </button>
            <button
              type="button"
              className="is-loss"
              disabled={etapa === 'perdida'}
              onClick={() => cambiarEtapa(p.nomenclatura, 'perdida')}
            >
              <Ban size={14} style={{ verticalAlign: '-2px', marginRight: 5 }} />
              Marcar perdida
            </button>
          </div>
          {etapa === 'perdida' && fila.motivo_cierre && (
            <div className="cal18-detail-desc" style={{ marginTop: 9, fontSize: 12 }}>
              Motivo del cierre: <b>{MOTIVO_LABEL[fila.motivo_cierre] || fila.motivo_cierre}</b>
            </div>
          )}

          <div className="cal18-datagrid">
            <div className="wide">
              <label>ENTIDAD</label>
              <p className="strong">{p.entidad || '—'}</p>
            </div>
            <div>
              <label>DEPARTAMENTO</label>
              <p>{p.departamento || '—'}</p>
            </div>
            <div>
              <label>OBJETO</label>
              <p>{p.objeto || '—'}</p>
            </div>
          </div>

          <div className="cal18-detail-section">FECHAS DEL PROCESO</div>
          <div className="cal18-crono">
            {hitos.map((tipo, i) => {
              const f = p.fechas[tipo];
              const pasado = f.getTime() < Date.now();
              const ultimo = i === hitos.length - 1;
              const esProximo = tipo === proximoHito;
              const nivel = nivelEvento(f);
              const badge = pasado
                ? { txt: 'cumplido', color: '#475569', bg: '#eef1f5' }
                : diasDesdeHoy(f) === 0
                ? { txt: 'hoy', color: '#b42318', bg: '#fdecea' }
                : diasDesdeHoy(f) === 1
                ? { txt: 'mañana', color: '#8a5308', bg: '#fdf3e6' }
                : { txt: etiquetaRelativa(f), color: '#475569', bg: '#eef1f5' };

              return (
                <React.Fragment key={tipo}>
                  <div className={`cal18-crono-rail ${TIPO_CLASS[tipo]} ${pasado ? 'is-past' : ''}`}>
                    <i />
                    {!ultimo && <u />}
                  </div>
                  <div className={`cal18-crono-cell ${ultimo ? 'is-last' : ''}`}>
                    <div className={`cal18-crono-label ${pasado ? 'is-past' : ''}`}>
                      <span>{TIPO_LABEL[tipo]}</span>
                      <span className="cal18-crono-badge" style={{ color: badge.color, background: badge.bg }}>
                        {badge.txt}
                      </span>
                    </div>
                    <div className="cal18-crono-date">{fmtFechaHora(f)}</div>

                    {esProximo && cd && (
                      <div className="cal18-countdown">
                        <div className="cal18-countdown-top">
                          <span>Tiempo restante</span>
                          <b
                            style={{
                              color: nivel === 'urg' ? '#b42318' : nivel === 'mid' ? '#8a5308' : '#0f766e'
                            }}
                          >
                            {cd.label}
                          </b>
                        </div>
                        <div className="cal18-countdown-bar">
                          <span className={`lv-${nivel}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div className="cal18-detail-section">SEGUIMIENTO INTERNO</div>
          <div className="sg28-fields">
            {editando && editando.campo === 'monto_estimado' ? (
              <div className="sg28-field-edit">
                <Wallet size={16} />
                <input
                  autoFocus
                  inputMode="decimal"
                  placeholder="Monto estimado en soles"
                  value={editando.valor}
                  onChange={(e) => setEditCampo({ ...editando, valor: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') guardarCampo();
                    if (e.key === 'Escape') setEditCampo(null);
                  }}
                />
                <button type="button" onClick={guardarCampo}>Guardar</button>
              </div>
            ) : (
              <button
                type="button"
                className="sg28-field"
                onClick={() =>
                  setEditCampo({
                    nomenclatura: p.nomenclatura,
                    campo: 'monto_estimado',
                    valor: fila.monto_estimado != null ? String(fila.monto_estimado) : ''
                  })
                }
              >
                <Wallet size={16} />
                <span>Monto estimado</span>
                <b className={fila.monto_estimado == null ? 'is-empty' : ''}>
                  {fila.monto_estimado == null ? 'Añadir' : fmtMontoLargo(fila.monto_estimado)}
                </b>
              </button>
            )}

            {editando && editando.campo === 'responsable' ? (
              <div className="sg28-field-edit">
                <User size={16} />
                <input
                  autoFocus
                  placeholder="¿Quién lo lleva?"
                  value={editando.valor}
                  onChange={(e) => setEditCampo({ ...editando, valor: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') guardarCampo();
                    if (e.key === 'Escape') setEditCampo(null);
                  }}
                />
                <button type="button" onClick={guardarCampo}>Guardar</button>
              </div>
            ) : (
              <button
                type="button"
                className="sg28-field"
                onClick={() =>
                  setEditCampo({
                    nomenclatura: p.nomenclatura,
                    campo: 'responsable',
                    valor: fila.responsable || ''
                  })
                }
              >
                <User size={16} />
                <span>Responsable</span>
                {fila.responsable ? (
                  <span className="sg28-field-person">
                    <i className="sg28-avatar">{inicial(fila.responsable)}</i>
                    <b>{fila.responsable}</b>
                  </span>
                ) : (
                  <b className="is-empty">Asignar</b>
                )}
              </button>
            )}

            <div className="sg28-field is-static">
              <div className="sg28-notes-head">
                <StickyNote size={16} />
                <span>Notas{notas.length ? ` (${notas.length})` : ''}</span>
                <button type="button" onClick={() => setNotaAbierta((v) => !v)}>
                  {notaAbierta ? 'Cancelar' : 'Añadir'}
                </button>
              </div>

              {notaAbierta && (
                <div className="sg28-note-form">
                  <textarea
                    autoFocus
                    placeholder="Qué falta, con quién hablaste, qué acordaron…"
                    value={notaTexto}
                    onChange={(e) => setNotaTexto(e.target.value)}
                  />
                  <div>
                    <button
                      className="cal18-btn cal18-btn-primary"
                      onClick={() => guardarNota(p.nomenclatura, notaTexto)}
                      disabled={!notaTexto.trim()}
                    >
                      Guardar nota
                    </button>
                  </div>
                </div>
              )}

              {[...notas].reverse().map((n, i) => (
                <div className="sg28-note" key={`${n.fecha}-${i}`}>
                  {n.texto}
                  <small>
                    {n.autor || 'anónimo'} · {haceCuanto(new Date(n.fecha))}
                  </small>
                </div>
              ))}

              {notas.length === 0 && !notaAbierta && (
                <div className="sg28-note" style={{ color: '#8a94a6' }}>
                  Sin notas todavía.
                </div>
              )}
            </div>
          </div>

          <div className="cal18-detail-actions">
            <button
              className="cal18-btn cal18-btn-primary"
              onClick={() => window.open(SEACE_BUSCADOR, '_blank', 'noopener,noreferrer')}
              style={{ justifyContent: 'center' }}
            >
              <ExternalLink size={15} /> Abrir ficha en SEACE
            </button>
            <div className="cal18-detail-row">
              <button className="cal18-btn" onClick={exportarICS}>
                <BellRing size={15} /> Recordatorio
              </button>
              <button
                className="cal18-btn"
                onClick={() => {
                  if (window.confirm(`¿Quitar "${p.nomenclatura}" del calendario?`)) eliminarProceso(p.nomenclatura);
                }}
              >
                <Trash2 size={15} /> Quitar
              </button>
            </div>
          </div>
        </div>

        <div className="cal18-detail-foot">
          <Keyboard size={15} />
          <span>
            <b>J / K</b> recorre · <b>1–5</b> cambia etapa · <b>Esc</b> cierra
          </span>
        </div>
      </div>
    );
  };

  // --- Panel lateral de filtros (18a) ---
  const renderFiltros = () => (
    <>
      <div className="cal18-mini">
        <div className="cal18-mini-top">
          <button className="cal18-icobtn sm" onClick={() => cambiarMes(-1)} title="Mes anterior">
            <ChevronLeft size={16} />
          </button>
          <b>{fechaActual.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}</b>
          <button className="cal18-icobtn sm" onClick={() => cambiarMes(1)} title="Mes siguiente">
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="cal18-mini-grid">
          {WEEKDAYS_MINI.map((d, i) => (
            <span className="cal18-mini-wd" key={`${d}-${i}`}>{d}</span>
          ))}
          {dias.map((dia, i) => {
            const evs = eventosDelMes.get(dia.fecha.toDateString()) || [];
            const tipos = Array.from(new Set(evs.map((e) => e.tipo))).slice(0, 3);
            const esHoy = sameDay(dia.fecha, hoy);
            const esSel = sameDay(dia.fecha, diaSel);
            return (
              <button
                key={i}
                className={`cal18-mini-day ${dia.actual ? '' : 'is-other'} ${esHoy ? 'is-today' : ''} ${
                  esSel ? 'is-sel' : ''
                } ${evs.length ? 'has-ev' : ''}`}
                onClick={() => clicDiaMini(dia.fecha, evs.length > 0)}
                title={evs.length ? `${evs.length} evento(s)` : undefined}
              >
                {dia.fecha.getDate()}
                {tipos.length > 0 && (
                  <span className="cal18-mini-dots">
                    {tipos.map((t) => (
                      <i key={t} className={TIPO_CLASS[t]} />
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="cal18-mini-foot">
          <span>{eventosDelMes.size} días con eventos este mes</span>
          <button
            className="cal18-link"
            onClick={() => {
              setFechaActual(new Date());
              setDiaSel(null);
            }}
          >
            Ir a hoy
          </button>
        </div>
      </div>

      <div className="cal18-card">
        <div className="cal18-facet-head">FUENTE</div>
        <div className="cal18-facet-list">
          {FUENTES.map((f) => (
            <button key={f} className={`cal18-opt ${!fuentesOff[f] ? 'is-on' : ''}`} onClick={() => toggleFuente(f)}>
              <span className="cal18-check"><Check size={12} strokeWidth={3} /></span>
              <span className="cal18-opt-txt">{FUENTE_LABEL[f]}</span>
              <span className="cal18-opt-count">{conteoFuente[f] || 0}</span>
            </button>
          ))}
        </div>

        <div className="cal18-facet-sub">TIPO DE FECHA</div>
        <div className="cal18-facet-list tight">
          {TIPOS.map((t) => (
            <button key={t} className={`cal18-opt ${!tiposOff[t] ? 'is-on' : ''}`} onClick={() => toggleTipo(t)}>
              <span className="cal18-check"><Check size={12} strokeWidth={3} /></span>
              <span className={`cal18-dot ${TIPO_CLASS[t]}`} style={{ width: 8, height: 8 }} />
              <span className="cal18-opt-txt">{TIPO_LABEL[t]}</span>
              <span className="cal18-opt-count">{conteoTipo[t] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="cal18-remind">
        <div className="cal18-remind-title">RECORDATORIOS</div>
        <button className="cal18-remind-row" onClick={exportarICS}>
          <BellRing size={17} />
          <div>
            <b>Descargar .ics con avisos</b>
            <small>Un aviso 3 días y otro 1 día antes de cada hito</small>
          </div>
        </button>
        <button
          className="cal18-remind-row"
          onClick={() => window.open(GOOGLE_IMPORT, '_blank', 'noopener,noreferrer')}
        >
          <CalendarPlus size={17} />
          <div>
            <b>Sincronizar con Google Calendar</b>
            <small>Importa allí el .ics que descargaste</small>
          </div>
        </button>
      </div>
    </>
  );

  // Punto 29: tarjeta de altura estable.
  const tecladoProceso = (e, it) => {
    if (e.target !== e.currentTarget) return;
    if (e.key >= '1' && e.key <= String(ETAPAS.length)) {
      e.preventDefault();
      e.stopPropagation();
      cambiarEtapa(it.proceso.nomenclatura, ETAPAS[Number(e.key) - 1]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      abrirProceso(it);
    }
  };

  const renderCard = (it) => {
    const p = it.proceso;
    const cerrada = ETAPAS_CERRADAS.includes(it.etapa);
    const tipoSel = it.proximo || TIPOS.find((t) => p.fechas[t]);
    const activa = drawerOpen && selKey === p.nomenclatura + '|' + tipoSel;
    const datosCompletos = !!it.fila.responsable && it.fila.monto_estimado != null;
    const cd = it.fechaProxima ? cuentaAtras(it.fechaProxima) : null;
    const urgente = !cerrada && cd && cd.ms > 0 && cd.ms < 86400000;
    const notas = (it.fila.notas || []).length;
    const cierre = [...segLog].reverse().find((log) => log.nomenclatura === p.nomenclatura && log.etapa_nueva === it.etapa);
    const fechaCierre = cierre ? new Date(cierre.creado_at) : null;
    // Reabrir una cerrada la devuelve a la etapa de la que salió; si la
    // bitácora no lo sabe (o venía de otra cerrada), cae en «Cotización enviada».
    const etapaPrevia = normalizaEtapa(cierre?.etapa_anterior);
    const reabrirEn = ETAPAS_CERRADAS.includes(etapaPrevia) ? 'negociando' : etapaPrevia;
    const editando = editCampo?.nomenclatura === p.nomenclatura && editCampo.origen === 'tarjeta';
    const editar = (campo) => setEditCampo({ nomenclatura: p.nomenclatura, campo, valor: String(it.fila[campo] ?? ''), origen: 'tarjeta' });
    const abrirNota = () => { abrirProceso(it); setNotaAbierta(true); };
    const etiqueta = it.etapa === 'vendida' ? 'Buena pro'
      : it.etapa === 'perdida' ? 'Perdida · ' + (MOTIVO_LABEL[it.fila.motivo_cierre] || 'otro')
      : cd ? TIPO_CORTO[it.proximo] : 'Sin fechas vivas';
    const plazo = cerrada ? fmtDiaCorto(fechaCierre)
      : cd ? cd.label : it.ultimoHito ? 'cerró ' + fmtDiaCorto(it.ultimoHito) : '—';
    return (
      <article key={p.nomenclatura} tabIndex={0} aria-label={p.nomenclatura + ': ' + tituloCorto(p.descripcion_proceso)}
        draggable={!editando} onDragStart={(e) => onDragStart(e, p.nomenclatura)} onDragEnd={onDragEnd}
        onClick={() => abrirProceso(it)} onKeyDown={(e) => tecladoProceso(e, it)}
        className={`sg28-card ${ETAPA_CLASS[it.etapa]} ${activa ? 'is-active' : ''} ${dragNom === p.nomenclatura ? 'is-dragging' : ''} ${cerrada ? 'is-cerrada' : ''} ${urgente ? 'is-urgent' : ''} ${datosCompletos ? 'has-own-data' : ''}`}>
        <div className="sg28-card-in">
          <div className="sg28-card-top">
            <span className={'sg28-pref ' + (FUENTE_CLASS[p.fuente] || '')}>{FUENTE_CHIP[p.fuente]}</span>
            <span className="sg28-nom" title={p.nomenclatura}>{p.nomenclatura}</span>
            <GripVertical size={14} className="sg28-grip" />
            <button type="button" className="sg29-discard"
              title="Quitar del seguimiento" aria-label={'Quitar ' + p.nomenclatura + ' del seguimiento'}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`¿Quitar "${p.nomenclatura}" del calendario?`)) eliminarProceso(p.nomenclatura);
              }}>
              <Trash2 size={13} />
            </button>
          </div>
          <div className="sg28-card-desc" title={p.descripcion_proceso}>{tituloCorto(p.descripcion_proceso)}</div>
          <div className="sg28-card-ent" title={[p.entidad, p.departamento].filter(Boolean).join(' · ')}>
            <Building2 size={12} /><span>{[p.entidad ? entidadCorta(p.entidad) : '', p.departamento ? tituloCorto(p.departamento) : ''].filter(Boolean).join(' · ') || 'Entidad por identificar'}</span>
          </div>
          <div className={'sg29-deadline ' + (urgente ? 'is-urgent' : '')} title={cerrada ? fmtFecha(fechaCierre) : fmtFechaHora(it.fechaProxima)}>
            {it.etapa === 'vendida' ? <Award size={13} /> : it.etapa === 'perdida' ? <Ban size={13} /> : urgente ? <Timer size={13} /> : <CalendarClock size={13} />}
            <span>{etiqueta}</span><b>{plazo}</b>
          </div>
          <div className={'sg29-footer ' + (editando ? 'is-editing' : '')} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {editando ? (
              <form className="sg29-inline-edit" onSubmit={(e) => { e.preventDefault(); guardarCampo(); }}>
                <input autoFocus aria-label={editCampo.campo === 'responsable' ? 'Responsable' : 'Monto estimado'}
                  type={editCampo.campo === 'monto_estimado' ? 'number' : 'text'} min={editCampo.campo === 'monto_estimado' ? '0' : undefined}
                  step={editCampo.campo === 'monto_estimado' ? '0.01' : undefined} value={editCampo.valor}
                  onChange={(e) => setEditCampo({ ...editCampo, valor: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditCampo(null); }} />
                <button type="submit" aria-label="Guardar campo"><Check size={14} /></button>
                <button type="button" aria-label="Cancelar edición" onClick={() => setEditCampo(null)}><X size={14} /></button>
              </form>
            ) : <>
              <div className="sg29-card-data">
                <button className={'sg29-owner ' + (it.fila.responsable ? '' : 'is-empty')} onClick={() => editar('responsable')} title={it.fila.responsable || 'Asignar responsable'}>
                  {it.fila.responsable ? <i className="sg28-avatar">{inicial(it.fila.responsable)}</i> : <User size={13} />}
                  <span>{it.fila.responsable || 'Asignar'}</span>
                </button>
                {notas > 0 && <button className="sg29-notes" onClick={abrirNota} aria-label={'Ver ' + notas + ' notas'}><StickyNote size={12} />{notas}</button>}
                <button className={'sg29-amount ' + (it.fila.monto_estimado == null ? 'is-empty ' : '') + (it.etapa === 'perdida' ? 'is-lost' : '')}
                  onClick={() => editar('monto_estimado')} title="Editar monto estimado">
                  {it.fila.monto_estimado == null ? <><Wallet size={13} />Monto</> : fmtMontoCorto(it.monto)}
                </button>
              </div>
              <button className="sg29-more-actions" aria-label="Acciones del proceso" aria-expanded={accionesTarjeta === p.nomenclatura}
                onClick={() => setAccionesTarjeta(accionesTarjeta === p.nomenclatura ? null : p.nomenclatura)}><MoreHorizontal size={14} /></button>
              <div className={'sg29-quick-actions ' + (accionesTarjeta === p.nomenclatura ? 'is-open' : '')}>
                <button onClick={abrirNota}><StickyNote size={12} />Nota</button>
                <a href={SEACE_BUSCADOR} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} />SEACE</a>
                {cerrada
                  ? <button onClick={() => cambiarEtapa(p.nomenclatura, reabrirEn)} title={'Devolver a «' + ETAPA_LABEL[reabrirEn] + '»'}><ChevronLeft size={12} />Reabrir</button>
                  : <button onClick={() => cambiarEtapa(p.nomenclatura, ETAPAS[ETAPAS.indexOf(it.etapa) + 1])}>Avanzar<ChevronRight size={12} /></button>}
              </div>
            </>}
          </div>
        </div>
      </article>
    );
  };

  const renderTablero = () => (
    <div className={`sg28-board ${dragNom ? 'is-dragging' : ''}`}>
      {ETAPAS.map((etapa) => {
        const items = columnas[etapa] || [];
        const abierta = colAbierta[etapa];
        const visibles = abierta ? items : items.slice(0, 4);
        const extra = items.length - visibles.length;
        const total = items.reduce((s, it) => s + it.monto, 0);
        const recibiendo = dragOver === etapa && dragNom && etapaDe(dragNom) !== etapa;

        return (
          <div
            key={etapa}
            className={`sg28-col ${ETAPA_CLASS[etapa]} ${recibiendo ? 'is-over' : ''}`}
            onDragEnter={(e) => onDragOverCol(e, etapa)}
            onDragOver={(e) => onDragOverCol(e, etapa)}
            onDragLeave={(e) => onDragLeaveCol(e, etapa)}
            onDrop={(e) => onDropCol(e, etapa)}
          >
            <div className="sg28-col-head" title={ETAPA_AYUDA[etapa]}>
              <i />
              <b>{ETAPA_LABEL[etapa]}</b>
              <span>{items.length}</span>
            </div>

            {/* Cartel de destino: va encima de la columna entera y sin
                pointer-events, así no desplaza las tarjetas ni roba el drop */}
            {recibiendo && (
              <div className="sg28-drop">
                <span className="sg28-drop-badge">
                  <MoveDown size={17} />
                  {etapa === 'vendida'
                    ? 'Soltar para marcar buena pro'
                    : etapa === 'perdida'
                    ? 'Soltar y elegir el motivo'
                    : `Soltar para mover a ${ETAPA_LABEL[etapa]}`}
                </span>
              </div>
            )}

            <div className="sg28-col-body">
              <div className="sg28-col-total">
                {total > 0 ? fmtMontoCorto(total) : ''}
                {ETAPAS_ABIERTAS.includes(etapa) && ' · ' + items.filter((it) => it.fechaProxima && it.fechaProxima - Date.now() <= 7 * 86400000).length + ' cierran ≤ 7 d'}
              </div>

              {visibles.map(renderCard)}

              {items.length === 0 && (
                <div className="sg28-col-empty">
                  {etapa === ETAPA_INICIAL ? 'Aquí llega lo que guardas del calendario' : 'Arrastra aquí un proceso'}
                </div>
              )}

              {extra > 0 && (
                <button className="sg28-more" onClick={() => setColAbierta((prev) => ({ ...prev, [etapa]: true }))}>
                  {'+' + extra + ' más'}
                </button>
              )}
              {abierta && items.length > 4 && (
                <button className="sg28-more" onClick={() => setColAbierta((prev) => ({ ...prev, [etapa]: false }))}>
                  Ver menos
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="cal18">
      {toast && <div className={`cal18-toast is-${toast.type}`}>{toast.message}</div>}

      {view === 'tablero' ? (
        <>
          {/* ── Cabecera 28a ─────────────────────────────── */}
          <div className="cal18-head">
            <div style={{ minWidth: 0 }}>
              <div className="cal18-eyebrow">MI SEGUIMIENTO · SEACE · MENORES</div>
              <h1 className="cal18-title">Seguimiento de convocatorias</h1>
              <div className="cal18-sub">
                {procesos.length} {procesos.length === 1 ? 'convocatoria activa' : 'convocatorias activas'} ·{' '}
                {procesosConFechas} con fechas vivas
                {horaSync && ` · sincronizado ${horaSync}`}
              </div>
            </div>
            <div className="cal18-head-actions">
              {segmentado}
              <button className="cal18-btn" onClick={exportarCSV} disabled={eventosVisibles.length === 0}>
                <FileDown size={15} />
                Exportar
              </button>
              <button className="cal18-btn cal18-btn-primary" onClick={() => navigate('/SEACE')}>
                <Plus size={15} />
                Agregar proceso
              </button>
            </div>
          </div>

          {segError && (
            <div className="sg28-banner">
              <AlertTriangle size={17} />
              <div>
                <b>El pipeline todavía no tiene dónde guardarse</b>
                <p>
                  Falta crear las tablas <code>seguimiento</code> y <code>seguimiento_log</code> en Supabase. Corre{' '}
                  <code>supabase/migrations/202609120010_seguimiento.sql</code> en el SQL Editor y recarga la página.
                  Mientras tanto las etapas no se guardan. ({segError})
                </p>
              </div>
            </div>
          )}

          {/* ── Indicadores del pipeline ─────────────────── */}
          <div className="sg28-kpis">
            <div className="sg28-kpi">
              <div className="sg28-kpi-eyebrow">EN TU CALENDARIO</div>
              <div className="sg28-kpi-pair">
                <span>
                  <b>{kpi.total}</b>
                  {kpi.total === 1 ? 'convocatoria' : 'convocatorias'}
                </span>
                <span>
                  <b>{kpi.abiertas}</b>
                  {kpi.abiertas === 1 ? 'abierta' : 'abiertas'}
                </span>
              </div>
              <div className="sg28-kpi-note">
                {kpi.total === 0
                  ? 'Guárdalas desde SEACE o Contratos Menores con el icono de calendario.'
                  : FUENTES.filter((f) => kpi.porFuente[f])
                      .map((f) => `${kpi.porFuente[f]} de ${FUENTE_LABEL[f]}`)
                      .join(' · ')}
              </div>
            </div>

            <div className="sg28-kpi">
              <div className="sg28-kpi-eyebrow">CIERRAN HOY Y MAÑANA</div>
              <div className="sg28-kpi-pair">
                <span>
                  <b className={kpi.urgentes ? 'is-urg' : ''}>{kpi.urgentes}</b>
                  {kpi.urgentes === 1 ? 'proceso' : 'procesos'}
                </span>
              </div>
              <div className={`sg28-kpi-flag ${kpi.urgentes ? '' : 'is-calm'}`}>
                <Timer size={14} />
                {resumen.proximo ? `el más próximo en ${resumen.proximo.label}` : 'nada en las próximas 24 h'}
              </div>
              {kpi.etapaUrgenteTop && (
                <div className="sg28-kpi-note">
                  {kpi.etapaUrgenteTop[1] === kpi.urgentes ? 'Todos están en ' : 'La mayoría están en '}
                  <b>{ETAPA_LABEL[kpi.etapaUrgenteTop[0]]}</b>
                </div>
              )}
            </div>

            <div className="sg28-kpi">
              <div className="sg28-kpi-eyebrow">
                CERRADAS EN {hoy.toLocaleDateString('es-PE', { month: 'long' }).toUpperCase()}
              </div>
              <div className="sg28-kpi-pair">
                <span>
                  <b className="is-win">{kpi.ganadas}</b>
                  {kpi.ganadas === 1 ? 'ganada' : 'ganadas'}
                </span>
                <span>
                  <b className="is-loss">{kpi.perdidas}</b>
                  {kpi.perdidas === 1 ? 'perdida' : 'perdidas'}
                </span>
              </div>
              <div className="sg28-kpi-note">
                {kpi.cerradas === 0 ? (
                  'Todavía no cierras ningún proceso este mes.'
                ) : (
                  <>
                    Tasa {kpi.tasa}%
                    {kpi.motivoTop ? (
                      <>
                        {' '}· motivo más frecuente: <b>{MOTIVO_LABEL[kpi.motivoTop[0]] || kpi.motivoTop[0]}</b>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <div className="sg28-kpi">
              <div className="sg28-kpi-eyebrow">TIEMPO MEDIO EN ETAPA</div>
              <div className="sg28-kpi-rows">
                {kpi.tiempos.map((t) => (
                  <div className="sg28-kpi-row" key={t.etapa}>
                    <span>{ETAPA_LABEL[t.etapa]}</span>
                    <u>
                      <i
                        style={{
                          width: `${t.dias ? Math.max(4, (t.dias / kpi.maxDias) * 100) : 0}%`,
                          background: ETAPA_HEX[t.etapa]
                        }}
                      />
                    </u>
                    <b className={t.dias && t.dias >= 7 ? 'is-slow' : ''}>
                      {t.dias == null ? '—' : `${Math.round(t.dias)} d`}
                    </b>
                  </div>
                ))}
              </div>
              <div className="sg28-kpi-note">
                {kpi.tiempos[0] && kpi.tiempos[0].dias >= 7
                  ? `${Math.round(kpi.tiempos[0].dias)} días sin revisar es donde se te van las convocatorias.`
                  : 'Se mide desde que el proceso entró al calendario.'}
              </div>
            </div>
          </div>

          {/* ── Barra del pipeline ───────────────────────── */}
          <div className="sg28-toolbar">
            <div style={{ minWidth: 0 }}>
              <div className="sg28-toolbar-title">Pipeline de seguimiento</div>
              <div className="sg28-toolbar-sub">
                Arrastra una tarjeta para cambiar su etapa comercial · también con las teclas 1–5
              </div>
            </div>
            <span className="cal18-spacer" style={{ flex: 1 }} />

            <div className="cal18-search">
              <Search size={15} />
              <input
                placeholder="Buscar código o entidad"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            <div className="sg28-filters">
              <label className="sg28-select">
                <List size={15} />
                <select aria-label="Orden del pipeline" value={ordenPipeline} onChange={(e) => setOrdenPipeline(e.target.value)}>
                  <option value="cierre">Cierre más próximo</option>
                  <option value="guardado">Orden guardado</option>
                </select>
              </label>
              <label className="sg28-select">
                <Filter size={15} />
                <select value={fuenteFiltro} onChange={(e) => setFuenteFiltro(e.target.value)}>
                  <option value="">Todas las fuentes</option>
                  {FUENTES.map((f) => (
                    <option key={f} value={f}>
                      {FUENTE_LABEL[f]}
                    </option>
                  ))}
                </select>
              </label>

              {responsables.length > 0 && (
                <label className="sg28-select">
                  <User size={15} />
                  <select value={respFiltro} onChange={(e) => setRespFiltro(e.target.value)}>
                    <option value="">Todos</option>
                    {responsables.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <button
                className={`sg28-toggle ${soloUrgentes ? 'is-on' : ''}`}
                onClick={() => setSoloUrgentes((v) => !v)}
              >
                <Zap size={14} />
                Solo con cierre ≤ 7 d
              </button>
            </div>
          </div>

          {procesos.length === 0 ? (
            <div className="cal18-card">
              <div className="cal18-empty">
                <div className="cal18-empty-ic"><Inbox size={22} /></div>
                <h4>No hay convocatorias en seguimiento</h4>
                <p>
                  Agrega procesos desde Convocatorias Vigentes o Contratos Menores con el botón «Añadir al
                  calendario».
                </p>
              </div>
            </div>
          ) : (
            renderTablero()
          )}

          {drawerOpen && (
            <div className="cal18-drawer" onClick={cerrarPanel}>
              <div className="cal18-drawer-inner" onClick={(e) => e.stopPropagation()}>
                {renderDetalle()}
              </div>
            </div>
          )}
        </>
      ) : view === 'cierres' ? (
        <>
          {/* ── Cabecera 18a ─────────────────────────────── */}
          <div className="cal18-head">
            <div style={{ minWidth: 0 }}>
              <div className="cal18-eyebrow">MI SEGUIMIENTO · SEACE · MENORES</div>
              <h1 className="cal18-title">Seguimiento de convocatorias</h1>
              <div className="cal18-sub">
                {procesos.length} {procesos.length === 1 ? 'convocatoria' : 'convocatorias'} en seguimiento ·{' '}
                {procesosConFechas} con fechas activas
                {horaSync && ` · sincronizado ${horaSync}`}
              </div>
            </div>
            <div className="cal18-head-actions">
              {segmentado}
              <button className="cal18-btn" onClick={exportarCSV} disabled={eventosVisibles.length === 0}>
                <FileDown size={15} />
                Exportar
              </button>
              <button className="cal18-btn cal18-btn-primary" onClick={() => navigate('/SEACE')}>
                <Plus size={15} />
                Agregar proceso
              </button>
            </div>
          </div>

          {/* ── Tarjetas de resumen (también filtran) ────── */}
          <div className="cal18-tiles">
            <button className={`cal18-tile t-urg ${tile === 'urg' ? 'is-on' : ''}`} onClick={() => clicTile('urg')}>
              <div className="cal18-tile-top">
                <BellRing size={16} />
                <span>CIERRAN HOY Y MAÑANA</span>
              </div>
              <div className="cal18-tile-body">
                <b>{resumen.urg}</b>
                <small>
                  {resumen.urg === 1 ? 'evento' : 'eventos'}
                  {resumen.proximo ? ` · el más próximo en ${resumen.proximo.label}` : ''}
                </small>
              </div>
            </button>

            <button className={`cal18-tile t-mid ${tile === 'sem' ? 'is-on' : ''}`} onClick={() => clicTile('sem')}>
              <div className="cal18-tile-top">
                <Timer size={16} />
                <span>PRÓXIMOS 7 DÍAS</span>
              </div>
              <div className="cal18-tile-body">
                <b>{resumen.sem}</b>
                <small>
                  {resumen.fines} fin{resumen.fines === 1 ? '' : 'es'} de registro · {resumen.ofertas} oferta
                  {resumen.ofertas === 1 ? '' : 's'}
                </small>
              </div>
            </button>

            <button className={`cal18-tile t-far ${tile === 'mes' ? 'is-on' : ''}`} onClick={() => clicTile('mes')}>
              <div className="cal18-tile-top">
                <CalendarClock size={16} />
                <span>RESTO DE {hoy.toLocaleDateString('es-PE', { month: 'long' }).toUpperCase()}</span>
              </div>
              <div className="cal18-tile-body">
                <b>{resumen.mes}</b>
                <small>en {resumen.diasMes} días con eventos</small>
              </div>
            </button>

            <button className={`cal18-tile t-old ${tile === 'old' ? 'is-on' : ''}`} onClick={() => clicTile('old')}>
              <div className="cal18-tile-top">
                <History size={16} />
                <span>YA CERRADOS</span>
              </div>
              <div className="cal18-tile-body">
                <b>{resumen.old}</b>
                <small>archivar o revisar buena pro</small>
              </div>
            </button>
          </div>

          {/* ── Agenda + panel lateral ───────────────────── */}
          <div className="cal18-grid">
            <section className="cal18-card">
              <div className="cal18-agenda-head">
                <div style={{ minWidth: 0 }}>
                  <div className="cal18-agenda-title">
                    {tile === 'old' ? 'Fechas ya cerradas' : 'Agenda de fechas críticas'}
                  </div>
                  <div className="cal18-agenda-sub">
                    {eventosVisibles.length} evento{eventosVisibles.length === 1 ? '' : 's'}
                    {diaSel ? ` el ${diaSel.toLocaleDateString('es-PE', { day: 'numeric', month: 'long' })}` : ''} ·
                    ordenados por fecha y hora
                  </div>
                </div>
                <span className="cal18-spacer" />
                <div className="cal18-search">
                  <Search size={15} />
                  <input
                    placeholder="Buscar código o entidad"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>
                {leyenda}
              </div>

              {(tile || diaSel) && (
                <div className="cal18-alert" style={{ background: '#f4f6f8', borderColor: '#e2e6ee' }}>
                  <CalendarDays size={16} />
                  <p style={{ color: '#5b6779' }}>
                    Filtro activo:{' '}
                    <b style={{ color: '#0f172a' }}>
                      {diaSel
                        ? diaSel.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })
                        : tile === 'urg'
                        ? 'cierran hoy y mañana'
                        : tile === 'sem'
                        ? 'próximos 7 días'
                        : tile === 'mes'
                        ? 'resto del mes'
                        : 'ya cerrados'}
                    </b>
                  </p>
                  <button
                    style={{ background: '#5b6779' }}
                    onClick={() => {
                      setTile(null);
                      setDiaSel(null);
                    }}
                  >
                    Quitar filtro
                  </button>
                </div>
              )}

              {inminente && !tile && !diaSel && (
                <div className="cal18-alert">
                  <AlertTriangle size={17} />
                  <p>
                    <b>
                      {diasDesdeHoy(inminente.ev.fecha) === 0 ? 'Hoy' : 'Mañana'} vence{' '}
                      {TIPO_LABEL[inminente.ev.tipo].toLowerCase()} de {inminente.ev.proceso.nomenclatura}.
                    </b>{' '}
                    Quedan {inminente.cd.label} para presentar en el SEACE.
                  </p>
                  <button onClick={() => abrirEvento(inminente.ev, true)}>Abrir proceso</button>
                </div>
              )}

              {eventosVisibles.length === 0 ? (
                <div className="cal18-empty">
                  <div className="cal18-empty-ic"><Inbox size={22} /></div>
                  <h4>{procesos.length === 0 ? 'No hay convocatorias en seguimiento' : 'Sin eventos con estos filtros'}</h4>
                  <p>
                    {procesos.length === 0
                      ? 'Agrega procesos desde Convocatorias Vigentes, o Contratos Menores con el botón «Añadir al calendario».'
                      : 'Prueba a quitar el filtro de la tarjeta, el día seleccionado o la búsqueda.'}
                  </p>
                </div>
              ) : (
                <>
                  {gruposAgenda.map(([dayKey, items]) => {
                    const fecha = new Date(dayKey);
                    const et = etiquetaDia(fecha);
                    return (
                      <React.Fragment key={dayKey}>
                        <div className="cal18-daysep">
                          <span className={`cal18-daysep-label ${et.cls}`}>{et.txt}</span>
                          <hr />
                          <span className="cal18-daysep-count">
                            {items.length} evento{items.length === 1 ? '' : 's'}
                          </span>
                        </div>

                        {items.map((ev) => {
                          const p = ev.proceso;
                          const nivel = nivelEvento(ev.fecha);
                          const etapaEv = etapaDe(p.nomenclatura);
                          const cerradaEv = ETAPAS_CERRADAS.includes(etapaEv);
                          return (
                            <div
                              key={ev.key}
                              className={`cal18-ev is-${nivel} ${selKey === ev.key ? 'is-active' : ''} ${
                                cerradaEv ? 'is-sent' : ''
                              }`}
                              onClick={() => abrirEvento(ev, true)}
                            >
                              <div className="cal18-ev-time">
                                <b>{fmtHM(ev.fecha)}</b>
                                <small>{etiquetaRelativa(ev.fecha)}</small>
                              </div>
                              <span className={`cal18-dot ${TIPO_CLASS[ev.tipo]}`} />

                              <div className="cal18-ev-main">
                                <div className="cal18-ev-tags">
                                  <span className="cal18-code">{p.nomenclatura}</span>
                                  <span className={`cal18-tag ${FUENTE_CLASS[p.fuente] || 'f-otro'}`}>
                                    {FUENTE_CHIP[p.fuente] || p.fuente}
                                  </span>
                                  <span className={`cal18-tag ${TIPO_CLASS[ev.tipo]}`}>{TIPO_LABEL[ev.tipo]}</span>
                                  <span style={{ flex: 1 }} />
                                  {renderPill(p.nomenclatura)}
                                </div>

                                <div className="cal18-ev-desc">{p.descripcion_proceso || 'Sin descripción'}</div>

                                <div className="cal18-ev-meta">
                                  <span title={p.entidad}>
                                    <Building2 size={14} />
                                    {p.entidad || '—'}
                                  </span>
                                  <span>
                                    <MapPin size={14} />
                                    {p.departamento || '—'}
                                  </span>
                                  {filaDe(p.nomenclatura).responsable && (
                                    <span>
                                      <User size={14} />
                                      {filaDe(p.nomenclatura).responsable}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="cal18-ev-actions" onClick={(e) => e.stopPropagation()}>
                                <button className="cal18-ev-cta" onClick={() => abrirEvento(ev, true)}>
                                  Ver detalle
                                </button>
                                <button
                                  className="cal18-icobtn"
                                  title="Abrir el buscador del SEACE"
                                  onClick={() => window.open(SEACE_BUSCADOR, '_blank', 'noopener,noreferrer')}
                                >
                                  <ExternalLink size={16} />
                                </button>
                                <button
                                  className="cal18-icobtn"
                                  title="Quitar del seguimiento"
                                  onClick={() => {
                                    if (window.confirm(`¿Quitar "${p.nomenclatura}" del calendario?`))
                                      eliminarProceso(p.nomenclatura);
                                  }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  <div className="cal18-listfoot">
                    <span>
                      Mostrando {eventosMostrados.length} de {eventosVisibles.length} eventos
                    </span>
                    {eventosVisibles.length > 6 && (
                      <button className="cal18-link" onClick={() => setVerTodos((v) => !v)}>
                        {verTodos ? 'Ver menos' : `Ver los ${eventosVisibles.length} eventos`}
                      </button>
                    )}
                  </div>
                </>
              )}
            </section>

            <aside className="cal18-side">{renderFiltros()}</aside>
          </div>

          {drawerOpen && (
            <div className="cal18-drawer" onClick={cerrarPanel}>
              <div className="cal18-drawer-inner" onClick={(e) => e.stopPropagation()}>
                {renderDetalle()}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── Cabecera 18b ─────────────────────────────── */}
          <div className="cal18-monthbar">
            <div className="cal18-monthbar-left">
              <button className="cal18-icobtn" onClick={() => cambiarMes(-1)} title="Mes anterior">
                <ChevronLeft size={17} />
              </button>
              <div>
                <h2>{fechaActual.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}</h2>
                <div className="cal18-monthbar-sub">
                  Hoy {hoy.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric' })} · {eventosHoy} evento
                  {eventosHoy === 1 ? '' : 's'} hoy · {cierresSemana} cierres esta semana
                </div>
              </div>
              <button className="cal18-icobtn" onClick={() => cambiarMes(1)} title="Mes siguiente">
                <ChevronRight size={17} />
              </button>
              <button className="cal18-btn cal18-btn-tint" onClick={() => setFechaActual(new Date())}>
                Hoy
              </button>
            </div>

            <div className="cal18-monthbar-right">
              {leyenda}
              {segmentado}
            </div>
          </div>

          <div className="cal18-grid-mes">
            <section className="cal18-card">
              <div className="cal18-monthgrid-wd">
                {WEEKDAYS_MES.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              <div className="cal18-monthgrid">
                {dias.map((dia, i) => {
                  const evs = (eventosDelMes.get(dia.fecha.toDateString()) || []).sort((a, b) => a.fecha - b.fecha);
                  const esHoy = sameDay(dia.fecha, hoy);
                  const visibles = evs.slice(0, 2);
                  const extra = evs.length - visibles.length;

                  return (
                    <div
                      key={i}
                      className={`cal18-cell ${dia.actual ? '' : 'is-other'} ${esHoy ? 'is-today' : ''}`}
                    >
                      <div className="cal18-cell-top">
                        <span className="cal18-cell-num">{dia.fecha.getDate()}</span>
                        {esHoy && <span className="cal18-cell-hoy">HOY</span>}
                      </div>

                      {visibles.map((ev) => {
                        const cd = cuentaAtras(ev.fecha);
                        const urgente = !!cd && cd.ms > 0 && cd.ms <= 86400000;
                        const etapaEv = etapaDe(ev.proceso.nomenclatura);
                        const cerradaEv = ETAPAS_CERRADAS.includes(etapaEv);
                        return (
                          <button
                            key={ev.key}
                            className={`cal18-chip ${TIPO_CLASS[ev.tipo]} ${urgente ? 'is-urgent' : ''} ${
                              selKey === ev.key ? 'is-active' : ''
                            } ${cerradaEv ? 'sg28-cerrada' : ''}`}
                            onClick={() => setSelKey(ev.key)}
                            title={`${ev.proceso.nomenclatura} — ${TIPO_LABEL[ev.tipo]} · ${ETAPA_LABEL[etapaEv]}`}
                          >
                            {/* El borde izquierdo es el tipo de hito; el punto, la etapa (28b) */}
                            <span className="sg28-chip-etapa" style={{ background: ETAPA_HEX[etapaEv] }} />
                            <div className="cal18-chip-top sg28-chip-pad">
                              {fmtHM(ev.fecha)} · {TIPO_CORTO[ev.tipo]}
                            </div>
                            <div className="cal18-chip-code">{ev.proceso.nomenclatura}</div>
                            {urgente ? (
                              <div className="cal18-chip-sub is-urgent">quedan {cd.label}</div>
                            ) : (
                              <div className="cal18-chip-sub">
                                {[ev.proceso.entidad, ev.proceso.departamento].filter(Boolean).join(' · ') || '—'}
                              </div>
                            )}
                          </button>
                        );
                      })}

                      {extra > 0 && (
                        <button className="cal18-cell-more" onClick={() => setSelKey(evs[visibles.length].key)}>
                          +{extra} más
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <aside>{renderDetalle()}</aside>
          </div>
        </>
      )}

      {/* ── Motivo de pérdida: el único cambio de etapa que pide un dato ── */}
      {modalPerdida && (
        <div className="sg28-modal" onClick={() => setModalPerdida(null)}>
          <div className="sg28-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="sg28-modal-head">
              <Ban size={17} color="#b42318" />
              <b>¿Por qué se perdió?</b>
              <button className="cal18-icobtn sm" onClick={() => setModalPerdida(null)} title="Cerrar">
                <X size={15} />
              </button>
            </div>
            <div className="sg28-modal-body">
              <p>
                Vas a cerrar <b>{modalPerdida.nomenclatura}</b>. El motivo es lo que después explica tu tasa de éxito.
              </p>
              <div className="sg28-motivos">
                {MOTIVOS.map((m) => (
                  <button
                    key={m.id}
                    className={modalPerdida.motivo === m.id ? 'is-on' : ''}
                    onClick={() => setModalPerdida({ ...modalPerdida, motivo: m.id })}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sg28-modal-foot">
              <button className="cal18-btn" onClick={() => setModalPerdida(null)}>
                Cancelar
              </button>
              <button className="cal18-btn cal18-btn-primary" onClick={confirmarPerdida}>
                <Check size={15} /> Marcar perdida
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendario;
